import React, {useEffect, useRef, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ActivityLog, ActivityType, Card, getActivityLogs, getAllCardsForUser, getCardById} from '../api';
import ResponsiveHeader from "../components/common/ResponsiveHeader";

// --- [스타일] 페이지 레이아웃 ---
const SearchSectionWrapper = styled.div` display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; `;

// ✨ [신규] CREATE 타입 전용 컨테이너
const CreationBox = styled.div`
  margin-top: 1rem;
  background-color: rgba(97, 175, 239, 0.05);
  border: 1px solid rgba(97, 175, 239, 0.2);
  border-radius: 6px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
`;

const CreationHierarchy = styled.div`
  font-size: 0.85rem;
  color: var(--text-dark);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  span.highlight {
    color: var(--primary-accent);
    font-weight: 600;
  }
`;

const CreationName = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-bright);
  word-break: break-all;
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg-main);
  color: var(--text-bright);
`;


const DateInput = styled.input`
  background-color: var(--bg-input);
  color: var(--text-bright);
  border: 1px solid var(--border-color);
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }

  @media (max-width: 768px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.8rem;
    width: 110px;
  }
`;

const CardSelect = styled.select`
  background-color: var(--bg-input);
  color: var(--text-bright);
  border: 1px solid var(--border-color);
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  max-width: 200px;

  option {
    background-color: var(--bg-card);
  }

  @media (max-width: 768px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.8rem;
    flex: 1;
    min-width: 100px;
    max-width: 150px;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--bg-list);
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// ✨ [수정] 선 제거 및 여백 대폭 확보
const TimelineContainer = styled.div`
  width: 100%;
  max-width: 900px;
  position: relative;

  /* 타임스탬프와 본문 사이 간격을 넓히기 위해 padding-left 증가 */
  padding-left: 160px;

  /* 수직선(::before) 제거함 */

  @media (max-width: 768px) {
    padding-left: 0; /* 모바일은 상단 배치이므로 왼쪽 여백 불필요 */
  }
`;

const TimelineItemWrapper = styled.div`
  position: relative;
  margin-bottom: 2.5rem; /* 항목 간 간격 조금 더 넓힘 */
  animation: fadeIn 0.3s ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// ✨ [수정] 타임스탬프 크고 선명하게, 멀리 배치
const TimeBadge = styled.div`
  position: absolute;
  left: -150px; /* 본문에서 훨씬 왼쪽으로 */
  top: 0;

  /* 폰트 스타일 강화 */
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-bright); /* 흐린 색 대신 밝은 색 사용 */

  text-align: right;
  width: 120px;
  line-height: 1.5rem;

  @media (max-width: 768px) {
    position: static;
    text-align: left;
    margin-bottom: 0.5rem;
    padding-left: 0.5rem;
    font-size: 1.1rem;
    width: auto;
    color: var(--primary-accent); /* 모바일에서는 포인트 컬러로 구분 */
  }
`;

// ✨ [수정] Dot 위치 미세 조정
const Dot = styled.div<{ type: ActivityType }>`
  position: absolute;
  left: -20px; /* 카드 바로 옆에 붙도록 */
  top: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.type === 'CREATE' ? 'var(--primary-accent)' : props.type === 'DELETE' ? 'var(--danger-accent)' : '#E5C07B'};
  border: 2px solid var(--bg-main);
  box-shadow: 0 0 0 2px ${props => props.type === 'CREATE' ? 'var(--primary-accent)' : props.type === 'DELETE' ? 'var(--danger-accent)' : '#E5C07B'}40;
  z-index: 1;

  @media (max-width: 768px) {
    display: none; /* 모바일에서는 타임스탬프가 위로 가므로 점 생략하여 깔끔하게 */
  }
`;

// ✨ [신규] 하이라이트 애니메이션
const flashAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(97, 175, 239, 0.7);
    border-color: var(--primary-accent);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(97, 175, 239, 0);
    border-color: var(--primary-accent);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(97, 175, 239, 0);
  }
`;

// ✨ [수정] LogCard에 cursor: pointer 추가 및 하이라이트 스타일 적용
const LogCard = styled.div<{ isHighlighted?: boolean }>`
  background-color: var(--bg-card);
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 4px var(--shadow-light);
  transition: all 0.2s;
  cursor: pointer; /* 클릭 가능 표시 */

  &:hover {
    border-color: var(--primary-accent);
    transform: translateY(-2px);
  }

  ${props => props.isHighlighted && css`
    border-color: var(--primary-accent);
    animation: ${flashAnimation} 1.5s ease-out infinite;
  `}
`;

const LogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const LogTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: bold;
  color: var(--text-bright);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span<{ type: ActivityType }>`
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background-color: ${props => props.type === 'CREATE' ? 'var(--primary-accent)' : props.type === 'DELETE' ? 'var(--danger-accent)' : '#E5C07B'}20;
  color: ${props => props.type === 'CREATE' ? 'var(--primary-accent)' : props.type === 'DELETE' ? 'var(--danger-accent)' : '#E5C07B'};
  border: 1px solid ${props => props.type === 'CREATE' ? 'var(--primary-accent)' : props.type === 'DELETE' ? 'var(--danger-accent)' : '#E5C07B'};
  font-weight: bold;
  white-space: nowrap;
`;

const CardLink = styled.span`
  font-size: 0.95rem;
  color: var(--text-normal);
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: var(--primary-accent);
    text-decoration: underline;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

// --- [스타일] 시각화 컴포넌트들 ---
const TodoContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--bg-input);
  overflow: hidden;
  margin-top: 0.25rem;
  width: 100%;
`;
const TodoItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--border-color);
  gap: 0.75rem;

  &:last-child {
    border-bottom: none;
  }
`;
const ReadOnlyCheckbox = styled.div<{ checked: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => props.checked ? 'var(--text-dark)' : 'var(--text-normal)'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.checked ? 'var(--text-dark)' : 'transparent'};
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
    stroke: var(--bg-input);
    stroke-width: 3;
  }
`;
const TodoText = styled.span<{ completed: boolean }>`
  color: ${props => props.completed ? 'var(--text-dark)' : 'var(--text-bright)'};
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
  font-size: 0.9rem;
  word-break: break-all;
`;
const LabelContainer = styled.div`display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;`;
const LabelPill = styled.div<{ color: string }>`
  background-color: ${props => props.color};
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
`;
const StateBadge = styled.span<{ bgColor: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  background-color: ${props => props.bgColor};
  color: #000;
  font-weight: bold;
  font-size: 0.8rem;
  display: inline-block;
  white-space: nowrap;
`;
const DescriptionPreview = styled.div`
  background-color: var(--bg-input);
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  font-size: 0.9rem;
  color: var(--text-normal);
  max-height: 200px;
  overflow-y: auto;

  p {
    margin: 0.25rem 0;
  }

  ul, ol {
    padding-left: 1.25rem;
    margin: 0.25rem 0;
  }

  strong {
    color: var(--text-bright);
  }
`;
const ReportLevelContainer = styled.div`display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;`;
const ReportLevelPill = styled.div`
  background-color: var(--bg-input);
  border: 1px solid var(--border-color);
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--text-normal);
`;
const ArrowSpan = styled.span`color: var(--text-dark);
  font-size: 0.8rem;`;

// --- Diff Viewer ---
const DiffContainer = styled.div`
  margin-top: 1.25rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 1rem;
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;
const DiffItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr auto 1fr;
  gap: 1rem;
  align-items: start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px dashed var(--border-color);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    & > span:nth-child(3) {
      display: flex;
      justify-content: center;
      transform: rotate(90deg);
      margin: 0.25rem 0;
    }
  }
`;
const FieldName = styled.span`color: var(--text-normal);
  font-weight: bold;
  font-size: 0.95rem;
  margin-top: 0.2rem;`;
const Arrow = styled.span`color: var(--text-dark);
  margin-top: 0.2rem;
  display: inline-block;`;
const OldValueBox = styled.div`
  color: var(--danger-accent);
  background-color: rgba(224, 108, 117, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9rem;
  word-break: break-all;
  min-width: 0;
`;
const NewValueBox = styled.div`
  color: var(--primary-accent);
  background-color: rgba(97, 175, 239, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9rem;
  word-break: break-all;
  min-width: 0;
`;

// Helper Functions
const getStateColor = (state?: string) => {
    switch (state) {
        case 'IDLE':
            return '#FFD54F';
        case 'FOLLOWUP':
            return '#BA68C8';
        case 'COMPLETED':
            return '#81C784';
        case 'PROCESS':
            return '#64B5F6';
        default:
            return 'var(--text-dark)';
    }
};
const getStateLabel = (state?: string) => {
    switch (state) {
        case 'IDLE':
            return '대기';
        case 'FOLLOWUP':
            return '팔로업';
        case 'COMPLETED':
            return '완료';
        case 'PROCESS':
            return '진행중';
        default:
            return state || '-';
    }
};

const ActivityLogPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const [cardList, setCardList] = useState<Card[]>([]);
    const [selectedCardId, setSelectedCardId] = useState<string>('all');

    const [cardNames, setCardNames] = useState<Record<string, string>>({});

    const containerRef = useRef<HTMLDivElement>(null);
    const logRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const validLogs = logs.filter(log => {
        if (log.type === 'UPDATE') {
            return log.diff && Object.keys(log.diff).length > 0;
        }
        return true;
    });

    // ✨ 4. 스크롤 이동 로직 (수정됨)
    useEffect(() => {
        const targetTimeParam = searchParams.get('time');

        if (targetTimeParam && validLogs.length > 0) {
            const targetSeconds = Number(targetTimeParam);

            let closestLogId = '';
            let minDiff = Infinity;

            // 가장 가까운 로그 찾기
            validLogs.forEach(log => {
                const logDate = new Date(log.timestamp);
                const logSeconds = logDate.getHours() * 3600 + logDate.getMinutes() * 60 + logDate.getSeconds();
                const diff = Math.abs(logSeconds - targetSeconds);

                if (diff < minDiff) {
                    minDiff = diff;
                    closestLogId = log.id;
                }
            });

            if (closestLogId) {
                // 약간의 지연 후 실행 (DOM 렌더링 확보)
                setTimeout(() => {
                    const container = containerRef.current;
                    const targetEl = logRefs.current[closestLogId];

                    if (container && targetEl) {
                        // ✨ [핵심] 컨테이너 내에서 타겟 요소의 상대 위치 계산
                        // TimelineContainer가 relative라서 offsetTop은 타임라인 시작점부터의 거리입니다.
                        // 화면 중앙에 오게 하기: 요소위치 - (컨테이너높이 / 2) + (요소높이 / 2)
                        const topPosition = targetEl.offsetTop - (container.clientHeight / 2) + (targetEl.clientHeight / 2);

                        container.scrollTo({
                            top: topPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 300);
            }
        }
    }, [validLogs, searchParams]);

    const handleLogClick = (timestamp: string) => {
        const date = new Date(timestamp);
        // 초 단위로 변환 (00:00:00 기준 흐른 초)
        const seconds = date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
        navigate(`/activity/playback?time=${seconds}`);
    };

    useEffect(() => {
        const fetchCards = async () => {
            try {
                const response = await getAllCardsForUser(0, 100);
                setCardList(response.data._embedded?.getList || []);
            } catch (error) {
                console.error("Failed to fetch cards", error);
            }
        };
        fetchCards();
    }, []);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await getActivityLogs(selectedDate, selectedCardId);
                setLogs(response.data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchLogs();
    }, [selectedDate, selectedCardId]);

    useEffect(() => {
        const fetchCardInfos = async () => {
            if (logs.length === 0) return;
            const uniqueIds = Array.from(new Set(logs
                .map(log => log.targetId)
                .filter(id => id && !cardNames[id])
            )) as string[];

            if (uniqueIds.length === 0) return;

            const newNames: Record<string, string> = {};
            const idsToFetch: string[] = [];

            uniqueIds.forEach(id => {
                const found = cardList.find(c => c.id === id);
                if (found) {
                    newNames[id] = found.title;
                } else {
                    idsToFetch.push(id);
                }
            });

            if (idsToFetch.length > 0) {
                await Promise.allSettled(idsToFetch.map(id => getCardById(id)))
                    .then(results => {
                        results.forEach((result, index) => {
                            const id = idsToFetch[index];
                            if (result.status === 'fulfilled') {
                                newNames[id] = result.value.data.title;
                            } else {
                                newNames[id] = '삭제된 카드';
                            }
                        });
                    });
            }
            setCardNames(prev => ({...prev, ...newNames}));
        };
        fetchCardInfos();
    }, [logs, cardList, cardNames]);

    const TodoListVisualizer = ({data}: { data: any }) => {
        if (!Array.isArray(data) || data.length === 0) return <span
            style={{color: 'var(--text-dark)', fontStyle: 'italic'}}>(Empty)</span>;
        return (
            <TodoContainer>
                {data.map((todo: any, idx: number) => (
                    <TodoItemRow key={idx}>
                        <ReadOnlyCheckbox checked={todo.isDone}>
                            {todo.isDone && <svg viewBox="0 0 24 24">
                                <polyline points="20 6 9 17 4 12" fill="none" stroke="currentColor"/>
                            </svg>}
                        </ReadOnlyCheckbox>
                        <TodoText completed={todo.isDone}>{todo.content}</TodoText>
                    </TodoItemRow>
                ))}
            </TodoContainer>
        );
    };

    const LabelVisualizer = ({data}: { data: any }) => {
        if (!Array.isArray(data) || data.length === 0) return <span
            style={{color: 'var(--text-dark)', fontStyle: 'italic'}}>(No Labels)</span>;
        return (
            <LabelContainer>
                {data.map((label: any, idx: number) => (
                    <LabelPill key={idx} color={label.color || '#61afef'}>{label.name}</LabelPill>
                ))}
            </LabelContainer>
        );
    };

    const StateVisualizer = ({data}: { data: any }) => {
        const stateStr = String(data);
        return <StateBadge bgColor={getStateColor(stateStr)}>{getStateLabel(stateStr)}</StateBadge>;
    };

    const DescriptionVisualizer = ({data}: { data: any }) => {
        if (!data) return <span style={{color: 'var(--text-dark)'}}>(Empty)</span>;
        return <DescriptionPreview dangerouslySetInnerHTML={{__html: String(data)}}/>;
    };

    const ReportLevelVisualizer = ({data}: { data: any }) => {
        if (!Array.isArray(data) || data.length === 0) return <span style={{color: 'var(--text-dark)'}}>-</span>;
        return (
            <ReportLevelContainer>
                {data.map((level: any, idx: number) => (
                    <React.Fragment key={idx}>
                        <ReportLevelPill>{level.name}</ReportLevelPill>
                        {idx < data.length - 1 && <ArrowSpan>→</ArrowSpan>}
                    </React.Fragment>
                ))}
            </ReportLevelContainer>
        );
    };

    const formatValue = (val: any) => {
        if (val === null || val === undefined) return <span style={{color: 'var(--text-dark)'}}>null</span>;
        if (typeof val === 'boolean') return val ? 'true' : 'false';
        if (typeof val === 'object') return JSON.stringify(val);
        return String(val);
    };

    const renderDiffValue = (key: string, value: any) => {
        switch (key) {
            case 'todoList':
                return <TodoListVisualizer data={value}/>;
            case 'labels':
                return <LabelVisualizer data={value}/>;
            case 'state':
                return <StateVisualizer data={value}/>;
            case 'description':
                return <DescriptionVisualizer data={value}/>;
            case 'reportLevels':
                return <ReportLevelVisualizer data={value}/>;
            case 'startDate':
            case 'dueDate':
                return <span style={{fontFamily: 'monospace'}}>📅 {formatValue(value)}</span>;
            default:
                return null;
        }
    };

    // ✨ 보드/리스트 이름 로드 로직 (on-demand)
    useEffect(() => {
        const fetchNames = async () => {
            if (logs.length === 0) return;

            const uniqueCardIds = Array.from(new Set(logs.filter(log => log.targetType === 'CARD' && log.targetId).map(l => l.targetId).filter(id => id))) as string[];
            const cardsToFetch = uniqueCardIds.filter(id => !cardNames[id]);

            if (cardsToFetch.length > 0) {
                await Promise.allSettled(cardsToFetch.map(id => getCardById(id))).then(results => {
                    results.forEach((res, idx) => {
                        if (res.status === 'fulfilled') {
                            const card = res.value.data;
                            setCardNames(prev => ({ ...prev, [card.id]: card.title }));

                            // 보드/리스트 이름이 없다면 추가로 가져옴 (간소화를 위해 API가 이름을 포함한다고 가정하거나 추가 호출)
                            // 여기서는 이미 cardList에 보드/리스트 ID가 있으므로 해당 ID들을 수집해 이름을 가져오는 로직이 추가될 수 있음
                        }
                    });
                });
            }
        };
        fetchNames();
    }, [logs, cardList, cardNames]);

    const SearchSection = (
        <SearchSectionWrapper>
            <DateInput type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}/>
            <CardSelect value={selectedCardId} onChange={(e) => setSelectedCardId(e.target.value)}>
                <option value="all">모든 카드 보기</option>
                {cardList.map(card => (
                    <option key={card.id} value={card.id}>{card.title}</option>
                ))}
            </CardSelect>
        </SearchSectionWrapper>
    );

    return (
        <PageWrapper>
            <ResponsiveHeader
                title="🕰 활동 로그"
                leftContent={SearchSection}
            />

            <ContentContainer ref={containerRef}>
                {validLogs.length === 0 ? (
                    <div style={{color: 'var(--text-dark)', marginTop: '2rem'}}>기록된 활동이 없습니다.</div>
                ) : (
                    <TimelineContainer>
                        {validLogs.map((log) => {
                                const isComment = log.targetType === 'COMMENT';

                                const targetTimeParam = searchParams.get('time');
                                let isHighlighted = false;

                                if (targetTimeParam) {
                                    // (간단히 가장 가까운 로그를 찾는 로직은 useEffect에서 했으므로,
                                    // 여기서는 완벽히 일치하거나 ref 스크롤 된 녀석을 시각적으로만 강조해도 됨.
                                    // 정확한 매칭을 위해선 위 useEffect 로직과 상태 공유가 필요하나,
                                    // 간단하게 선택된 시간과 1분 내외 차이면 하이라이트 처리)
                                    const logDate = new Date(log.timestamp);
                                    const logSeconds = logDate.getHours() * 3600 + logDate.getMinutes() * 60 + logDate.getSeconds();
                                    if (Math.abs(logSeconds - Number(targetTimeParam)) < 60) {
                                        isHighlighted = true;
                                    }
                                }

                                return (
                                    <TimelineItemWrapper key={log.id} ref={el => { logRefs.current[log.id] = el; }}>
                                        <TimeBadge>{log.timestamp.split('T')[1].substring(0, 8)}</TimeBadge>
                                        <Dot type={log.type}/>
                                        <LogCard onClick={() => !isComment && handleLogClick(log.timestamp)} isHighlighted={isHighlighted}>
                                            <LogHeader>
                                                <LogTitle>
                                                    <TypeBadge type={log.type}>{isComment ? 'COMMENT' : log.type}</TypeBadge>
                                                    {/* ✨ 코멘트일 때는 description(내용 포함)을 제목으로 사용 */}
                                                    {log.activity}
                                                </LogTitle>
                                                {!isComment && log.targetType === 'CARD' && log.targetId && (
                                                    <CardLink onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/tasks/card/${log.targetId}`)
                                                    }}>
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path
                                                                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                                            <path
                                                                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                                        </svg>
                                                        {cardNames[log.targetId] || 'Loading...'}
                                                    </CardLink>
                                                )}
                                            </LogHeader>

                                            {log.type === 'CREATE' && (
                                                <CreationBox>
                                                    <CreationHierarchy>
                                                        {/* Board, List 이름이 매핑되어 있지 않으면 ID 노출 대신 'Board', 'List' 텍스트 사용 */}
                                                        {log.targetType === 'CARD' ? (
                                                            <><span>Board</span> ➜ <span>List</span> ➜ <span className="highlight">Card</span></>
                                                        ) : (
                                                            <><span className="highlight">New Comment</span></>
                                                        )}
                                                    </CreationHierarchy>
                                                    <CreationName>
                                                        {/* ✨ 코멘트 생성 시 "삭제된 카드" 대신 description 표시 */}
                                                        {!isComment ? (cardNames[log.targetId] || '새 업무') : log.description}
                                                    </CreationName>
                                                    <div style={{fontSize: '0.85rem', color: 'var(--text-dark)'}}>
                                                        {isComment ? '코멘트' : '업무'}가 성공적으로 생성되었습니다.
                                                    </div>
                                                </CreationBox>
                                            )}

                                            {log.type === 'UPDATE' && log.diff && (
                                                <DiffContainer>
                                                    {Object.entries(log.diff).map(([key, value]) => {
                                                        const visualized = renderDiffValue(key, value.from);
                                                        return (
                                                            <DiffItem key={key}>
                                                                <FieldName>{key}</FieldName>
                                                                <div style={{minWidth: 0}}>
                                                                    {visualized ? renderDiffValue(key, value.from) :
                                                                        <OldValueBox>{formatValue(value.from)}</OldValueBox>}
                                                                </div>
                                                                <Arrow>➜</Arrow>
                                                                <div style={{minWidth: 0}}>
                                                                    {visualized ? renderDiffValue(key, value.to) :
                                                                        <NewValueBox>{formatValue(value.to)}</NewValueBox>}
                                                                </div>
                                                            </DiffItem>
                                                        );
                                                    })}
                                                </DiffContainer>
                                            )}
                                        </LogCard>
                                    </TimelineItemWrapper>
                                )
                            }
                        )}
                    </TimelineContainer>
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default ActivityLogPage;